import type { ApiClient } from "./client";
import type { CreateStaffRequest, PermissionOverrides, StaffUser, UpdateStaffRequest, AuditLog, CashierPerformance, DailySummary, ExpiringBatch, LowStockRow, MpesaFilters, MpesaTxn, RefundRequest, RevenueDay, Sale, SaleFilters, SaleRefund, SaleRequest, SyncResult, TopProduct, Batch, Branch, BranchPermissions, Branding, Category, CreditRepayment, Customer, CustomerInput, Dispenser, FuelReconciliation, InventoryRow, LoginResponse, MeterReading, Page, Plan, Product, ProductInput, ProductUnit, ReceiveBatch, SignupRequest, StockAdjustment, Tank, TillSession, User } from "./types";

/** Typed wrappers for §17.1, grouped by area. Screens call these (through React Query),
 * never raw paths, so a path or shape change is one edit. */
export function endpoints(c: ApiClient) {
  return {
    auth: {
      login: (username: string, password: string) => c.post<LoginResponse>("/auth/login", { username, password }),
      me: (token?: string) => c.get<User>("/auth/me", token ? { token } : undefined),
      changePassword: (currentPassword: string, newPassword: string) => c.post<{ message?: string }>("/auth/change-password", { currentPassword, newPassword }),
      forgotPassword: (usernameOrEmail: string) => c.post<{ message?: string; devResetToken?: string }>("/auth/forgot-password", { usernameOrEmail }),
      resetPassword: (token: string, newPassword: string) => c.post<{ message?: string }>("/auth/reset-password", { token, newPassword }),
      authorizeOverride: (username: string, password: string) => c.post<{ userId: number; fullName: string }>("/auth/authorize-override", { username, password }),
    },
    signup: {
      plans: () => c.get<Plan[]>("/tenants/plans"),
      create: (body: SignupRequest) => c.post<{ message?: string }>("/tenants/signup", body),
    },
    branding: {
      get: () => c.get<Branding>("/branding"),
      put: (b: Branding) => c.put<Branding>("/branding", b),
    },
    branches: {
      list: () => c.get<Branch[]>("/branches"),
      get: (id: number) => c.get<Branch>(`/branches/${id}`),
      put: (id: number, b: Omit<Branch, "id" | "name">) => c.put<Branch>(`/branches/${id}`, b),
    },
    permissions: {
      branch: () => c.get<BranchPermissions>("/branch-permissions"),
      putBranch: (p: BranchPermissions) => c.put<BranchPermissions>("/branch-permissions", p),
      /** What the signed-in user may do (§4.2: cashier UIs use this, never the branch one). */
      effective: () => c.get<Partial<BranchPermissions>>("/branch-permissions/effective"),
    },
    till: {
      current: () => c.get<TillSession | null>("/till/current"),
      lastClosed: () => c.get<TillSession | null>("/till/last-closed"),
      open: (openingFloat: number, notes: string | null) => c.post<TillSession>("/till/open", { openingFloat, notes }),
      close: (declaredCash: number, notes: string | null) => c.post<TillSession>("/till/close", { declaredCash, notes }),
      cashIn: (amount: number, reason: string) => c.post<TillSession>("/till/cash-in", { amount, reason }),
      cashOut: (amount: number, reason: string) => c.post<TillSession>("/till/cash-out", { amount, reason }),
      history: () => c.get<TillSession[]>("/till"),
      get: (id: number) => c.get<TillSession>(`/till/${id}`),
    },
    sales: {
      /** STK pushes wait for the customer's PIN, so M-Pesa sales get longer (§7.7). */
      create: (req: SaleRequest) => c.post<Sale>("/sales", req, { timeoutMs: req.payments.some((p) => p.method === "MPESA") ? 120_000 : 30_000 }),
      sync: (reqs: SaleRequest[]) => c.post<SyncResult[]>("/sales/sync", reqs),
      list: (f: SaleFilters) => c.get<Page<Sale>>("/sales", { query: f }),
      get: (id: number) => c.get<Sale>(`/sales/${id}`),
      refunds: (id: number) => c.get<SaleRefund[]>(`/sales/${id}/refunds`),
      refund: (id: number, r: RefundRequest) => c.post<Sale>(`/sales/${id}/refunds`, r),
      print: (id: number, printerHost: string, printerPort: number) => c.post<{ message?: string }>(`/sales/${id}/print`, { printerHost, printerPort }),
      submitEtims: (id: number) => c.post<{ message?: string; sale?: Sale }>(`/sales/${id}/etims/submit`),
    },
    mpesa: {
      list: (f: MpesaFilters) => c.get<Page<MpesaTxn>>("/payments/mpesa/transactions", { query: f }),
      recheck: (checkoutRequestId: string) => c.post<MpesaTxn>(`/payments/mpesa/transactions/${encodeURIComponent(checkoutRequestId)}/recheck`),
    },
    reports: {
      daily: () => c.get<DailySummary>("/reports/daily-summary"),
      lowStock: () => c.get<LowStockRow[]>("/reports/low-stock"),
      topProducts: () => c.get<TopProduct[]>("/reports/top-products"),
      expiringSoon: (days = 30) => c.get<ExpiringBatch[]>("/reports/expiring-soon", { query: { days } }),
      revenueByDay: (days: number) => c.get<RevenueDay[]>("/reports/revenue-by-day", { query: { days } }),
      cashierPerformance: (days: number) => c.get<CashierPerformance[]>("/reports/cashier-performance", { query: { days } }),
    },
    users: {
      list: () => c.get<StaffUser[]>("/users"),
      create: (b: CreateStaffRequest) => c.post<StaffUser>("/users", b),
      update: (id: number, b: UpdateStaffRequest) => c.put<StaffUser>(`/users/${id}`, b),
      resetPassword: (id: number, newPassword: string) => c.put<StaffUser>(`/users/${id}/password`, { newPassword }),
      /** null = "Branch default" (§4.3). */
      setPermissions: (id: number, p: PermissionOverrides) => c.put<StaffUser>(`/users/${id}/permissions`, p),
      deactivate: (id: number) => c.put<StaffUser>(`/users/${id}/deactivate`),
    },
    audit: {
      list: (q: { page?: number; size?: number }) => c.get<Page<AuditLog>>("/audit-logs", { query: q }),
    },
    products: {
      search: (q: string, opts: { page?: number; size?: number; includeInactive?: boolean; signal?: AbortSignal } = {}) =>
        c.get<Page<Product>>("/products", { query: { q, page: opts.page, size: opts.size, includeInactive: opts.includeInactive || undefined }, signal: opts.signal }),
      favorites: () => c.get<Product[]>("/products/favorites"),
      byCategory: (id: number) => c.get<Product[]>(`/products/by-category/${id}`),
      byBarcode: (code: string) => c.get<Product>(`/products/barcode/${encodeURIComponent(code)}`),
      get: (id: number) => c.get<Product>(`/products/${id}`),
      generateSku: (categoryId: number | null) => c.get<{ sku: string }>("/products/generate-sku", { query: { categoryId } }),
      create: (p: ProductInput) => c.post<Product>("/products", p),
      update: (id: number, p: ProductInput) => c.put<Product>(`/products/${id}`, p),
      deactivate: (id: number) => c.del<void>(`/products/${id}`),
      setFavorite: (id: number, favorite: boolean) => c.put<Product>(`/products/${id}/favorite`, { favorite }),
      units: (id: number) => c.get<ProductUnit[]>(`/products/${id}/units`),
      addUnit: (id: number, u: Omit<ProductUnit, "id">) => c.post<ProductUnit>(`/products/${id}/units`, u),
      deleteUnit: (id: number, unitId: number) => c.del<void>(`/products/${id}/units/${unitId}`),
    },
    categories: {
      list: () => c.get<Category[]>("/categories"),
      create: (b: { name: string; parentId: number | null }) => c.post<Category>("/categories", b),
      update: (id: number, b: { name: string; parentId: number | null }) => c.put<Category>(`/categories/${id}`, b),
      remove: (id: number) => c.del<void>(`/categories/${id}`),
    },
    inventory: {
      list: () => c.get<InventoryRow[]>("/inventory"),
      adjust: (a: StockAdjustment) => c.post<InventoryRow>("/inventory/adjust", a),
      batches: (q: { productId?: number; expiringWithinDays?: number } = {}) => c.get<Batch[]>("/inventory/batches", { query: q }),
      receiveBatch: (b: ReceiveBatch) => c.post<Batch>("/inventory/batches", b),
    },
    customers: {
      search: (q: string, opts: { page?: number; size?: number; signal?: AbortSignal } = {}) => c.get<Page<Customer>>("/customers", { query: { q, page: opts.page, size: opts.size }, signal: opts.signal }),
      get: (id: number) => c.get<Customer>(`/customers/${id}`),
      create: (b: CustomerInput) => c.post<Customer>("/customers", b),
      update: (id: number, b: CustomerInput) => c.put<Customer>(`/customers/${id}`, b),
      setCreditLimit: (id: number, creditLimit: number | null) => c.put<Customer>(`/customers/${id}/credit-limit`, { creditLimit }),
      repayments: (id: number) => c.get<CreditRepayment[]>(`/customers/${id}/credit/repayments`),
      recordRepayment: (id: number, b: { amount: number; method: CreditRepayment["method"]; notes: string | null }) => c.post<CreditRepayment>(`/customers/${id}/credit/repayments`, b),
    },
    fuel: {
      tanks: () => c.get<Tank[]>("/fuel/tanks"),
      createTank: (b: { productId: number; name: string; capacity: number; currentVolume: number }) => c.post<Tank>("/fuel/tanks", b),
      dispensers: () => c.get<Dispenser[]>("/fuel/dispensers"),
      createDispenser: (b: { tankId: number; name: string }) => c.post<Dispenser>("/fuel/dispensers", b),
      readings: (id: number) => c.get<MeterReading[]>(`/fuel/dispensers/${id}/readings`),
      recordReading: (id: number, value: number) => c.post<MeterReading>(`/fuel/dispensers/${id}/readings`, { value }),
      reconciliation: () => c.get<FuelReconciliation[]>("/fuel/reconciliation"),
    },
  };
}
export type Endpoints = ReturnType<typeof endpoints>;
